/**
 * 🔐 END-TO-END ENCRYPTION SERVICE
 *
 * Provides military-grade E2EE for messaging using:
 * - ECDH (P-256) for key exchange
 * - AES-256-GCM for encryption
 * - HMAC-SHA256 for message authentication
 * - HKDF for key derivation
 */

import {
  arrayBufferToBase64,
  base64ToArrayBuffer,
  stringToArrayBuffer,
  arrayBufferToString,
  generateRandomBytes,
  isWebCryptoAvailable,
} from "../utils/cryptoHelpers";

class E2EEService {
  constructor() {
    this.initialized = false;
    this.publicKey = null;
    this.privateKeyCache = null; // Cache for current session
    this.conversationKeysCache = new Map(); // Cache derived conversation keys
  }

  /**
   * Initialize E2EE - Check if Web Crypto is available
   */
  initialize() {
    if (!isWebCryptoAvailable()) {
throw new Error("Web Crypto API not available. Use HTTPS.");
    }
this.initialized = true;
  }

  /**
   * Generate ECDH key pair for user
   * @returns {Promise<{publicKey: string, privateKey: string}>}
   */
  async generateKeyPair() {
    try {
// Generate P-256 ECDH key pair
      const keyPair = await window.crypto.subtle.generateKey(
        {
          name: "ECDH",
          namedCurve: "P-256",
        },
        true, // extractable
        ["deriveKey", "deriveBits"]
      );

      // Export public key
      const publicKeyRaw = await window.crypto.subtle.exportKey(
        "spki",
        keyPair.publicKey
      );
      const publicKeyBase64 = arrayBufferToBase64(publicKeyRaw);

      // Export private key
      const privateKeyRaw = await window.crypto.subtle.exportKey(
        "pkcs8",
        keyPair.privateKey
      );
      const privateKeyBase64 = arrayBufferToBase64(privateKeyRaw);
return {
        publicKey: publicKeyBase64,
        privateKey: privateKeyBase64,
      };
    } catch (error) {
throw new Error("Failed to generate encryption keys");
    }
  }

  /**
   * Store encrypted private key in localStorage
   * @param {string} privateKey - Base64 private key
   * @param {string} password - User's password
   */
  async storePrivateKey(privateKey, password) {
    try {
// Generate salt
      const salt = generateRandomBytes(32);

      // Derive encryption key from password using PBKDF2
      const passwordKey = await this.deriveKeyFromPassword(
        password,
        salt,
        150000
      );

      // Generate IV
      const iv = generateRandomBytes(12);

      // Encrypt private key
      const encryptedData = await window.crypto.subtle.encrypt(
        { name: "AES-GCM", iv: iv },
        passwordKey,
        base64ToArrayBuffer(privateKey)
      );

      // Store in localStorage
      const encryptedPackage = {
        data: arrayBufferToBase64(encryptedData),
        salt: arrayBufferToBase64(salt),
        iv: arrayBufferToBase64(iv),
        version: "v1",
      };

      localStorage.setItem(
        "e2ee_private_key_encrypted",
        JSON.stringify(encryptedPackage)
      );
} catch (error) {
throw new Error("Failed to store private key");
    }
  }

  /**
   * Load and decrypt private key from localStorage
   * @param {string} password - User's password
   * @returns {Promise<string>} Decrypted private key (base64)
   */
  async loadPrivateKey(password) {
    try {
      // Check cache first
      if (this.privateKeyCache) {
return this.privateKeyCache;
      }
const encryptedData = JSON.parse(
        localStorage.getItem("e2ee_private_key_encrypted")
      );

      if (!encryptedData) {
        throw new Error("No encrypted private key found");
      }

      // Derive decryption key from password
      const passwordKey = await this.deriveKeyFromPassword(
        password,
        base64ToArrayBuffer(encryptedData.salt),
        150000
      );

      // Decrypt private key
      const decryptedData = await window.crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: base64ToArrayBuffer(encryptedData.iv),
        },
        passwordKey,
        base64ToArrayBuffer(encryptedData.data)
      );

      const privateKey = arrayBufferToBase64(decryptedData);

      // Cache for current session
      this.privateKeyCache = privateKey;
return privateKey;
    } catch (error) {
throw new Error("Failed to decrypt private key - wrong password?");
    }
  }

  /**
   * Derive encryption key from password using PBKDF2
   * @private
   */
  async deriveKeyFromPassword(password, salt, iterations) {
    const passwordKey = await window.crypto.subtle.importKey(
      "raw",
      typeof password === "string" ? stringToArrayBuffer(password) : password,
      "PBKDF2",
      false,
      ["deriveBits", "deriveKey"]
    );

    return await window.crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt,
        iterations: iterations,
        hash: "SHA-512",
      },
      passwordKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );
  }

  /**
   * Encrypt a message for a recipient
   * @param {string} plaintext - Message to encrypt
   * @param {string} recipientPublicKey - Recipient's public key (base64)
   * @param {string} conversationId - Conversation ID
   * @param {string} myPrivateKey - Sender's private key (base64)
   * @returns {Promise<Object>} Encrypted package
   */
  async encryptMessage(
    plaintext,
    recipientPublicKey,
    conversationId,
    myPrivateKey
  ) {
    try {
// 1. Derive shared secret using ECDH
      const sharedSecret = await this.deriveSharedSecret(
        myPrivateKey,
        recipientPublicKey
      );

      // 2. Derive conversation-specific key using HKDF
      const conversationKey = await this.deriveConversationKey(
        sharedSecret,
        conversationId
      );

      // 3. Generate random IV (12 bytes for GCM)
      const iv = generateRandomBytes(12);

      // 4. Encrypt with AES-256-GCM
      const encryptedData = await window.crypto.subtle.encrypt(
        {
          name: "AES-GCM",
          iv: iv,
        },
        conversationKey,
        stringToArrayBuffer(plaintext)
      );

      const ciphertext = arrayBufferToBase64(encryptedData);

      // 5. Generate HMAC signature
      const signature = await this.generateHMAC(ciphertext, conversationKey);

      // 6. Create encrypted package
      const encryptedPackage = {
        ciphertext,
        iv: arrayBufferToBase64(iv),
        signature,
        version: "v1",
        algorithm: "aes-256-gcm",
        timestamp: Date.now(),
      };
return encryptedPackage;
    } catch (error) {
throw new Error("Failed to encrypt message");
    }
  }

  /**
   * Decrypt a message from a sender
   * @param {Object} encryptedPackage - Encrypted message package
   * @param {string} senderPublicKey - Sender's public key (base64)
   * @param {string} conversationId - Conversation ID
   * @param {string} myPrivateKey - Recipient's private key (base64)
   * @returns {Promise<string>} Decrypted plaintext
   */
  async decryptMessage(
    encryptedPackage,
    senderPublicKey,
    conversationId,
    myPrivateKey
  ) {
    try {
const { ciphertext, iv, signature } = encryptedPackage;

      // 1. Derive shared secret using ECDH
      const sharedSecret = await this.deriveSharedSecret(
        myPrivateKey,
        senderPublicKey
      );

      // 2. Derive conversation-specific key
      const conversationKey = await this.deriveConversationKey(
        sharedSecret,
        conversationId
      );

      // 3. Verify HMAC signature
      const isValid = await this.verifyHMAC(
        ciphertext,
        signature,
        conversationKey
      );

      if (!isValid) {
        throw new Error("Signature verification failed - possible tampering!");
      }

      // 4. Decrypt with AES-256-GCM
      const decryptedData = await window.crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: base64ToArrayBuffer(iv),
        },
        conversationKey,
        base64ToArrayBuffer(ciphertext)
      );

      const plaintext = arrayBufferToString(decryptedData);
return plaintext;
    } catch (error) {
if (error.message.includes("signature")) {
        throw new Error("Message integrity check failed");
      }

      throw new Error("Failed to decrypt message");
    }
  }

  /**
   * Derive shared secret using ECDH
   * @private
   */
  async deriveSharedSecret(privateKeyBase64, publicKeyBase64) {
    // Import private key
    const privateKey = await window.crypto.subtle.importKey(
      "pkcs8",
      base64ToArrayBuffer(privateKeyBase64),
      {
        name: "ECDH",
        namedCurve: "P-256",
      },
      false,
      ["deriveKey", "deriveBits"]
    );

    // Import public key
    const publicKey = await window.crypto.subtle.importKey(
      "spki",
      base64ToArrayBuffer(publicKeyBase64),
      {
        name: "ECDH",
        namedCurve: "P-256",
      },
      false,
      []
    );

    // Derive shared secret
    const sharedSecret = await window.crypto.subtle.deriveBits(
      {
        name: "ECDH",
        public: publicKey,
      },
      privateKey,
      256
    );

    return sharedSecret;
  }

  /**
   * Derive conversation-specific key using HKDF
   * @private
   */
  async deriveConversationKey(sharedSecret, conversationId) {
    // Check cache
    const cacheKey = `${arrayBufferToBase64(sharedSecret)}_${conversationId}`;
    if (this.conversationKeysCache.has(cacheKey)) {
      return this.conversationKeysCache.get(cacheKey);
    }

    // Import shared secret as base key
    const baseKey = await window.crypto.subtle.importKey(
      "raw",
      sharedSecret,
      "HKDF",
      false,
      ["deriveKey"]
    );

    // Derive conversation-specific key using HKDF
    const conversationKey = await window.crypto.subtle.deriveKey(
      {
        name: "HKDF",
        hash: "SHA-256",
        salt: stringToArrayBuffer(conversationId),
        info: stringToArrayBuffer("ResearchPro-E2EE-v1"),
      },
      baseKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );

    // Cache it
    this.conversationKeysCache.set(cacheKey, conversationKey);

    return conversationKey;
  }

  /**
   * Generate HMAC-SHA256 signature
   * @private
   */
  async generateHMAC(ciphertext, conversationKey) {
    // Export conversation key for HMAC
    const keyMaterial = await window.crypto.subtle.exportKey(
      "raw",
      conversationKey
    );

    const hmacKey = await window.crypto.subtle.importKey(
      "raw",
      keyMaterial,
      {
        name: "HMAC",
        hash: "SHA-256",
      },
      false,
      ["sign"]
    );

    const signature = await window.crypto.subtle.sign(
      "HMAC",
      hmacKey,
      stringToArrayBuffer(ciphertext)
    );

    return arrayBufferToBase64(signature);
  }

  /**
   * Verify HMAC-SHA256 signature
   * @private
   */
  async verifyHMAC(ciphertext, signature, conversationKey) {
    try {
      const keyMaterial = await window.crypto.subtle.exportKey(
        "raw",
        conversationKey
      );

      const hmacKey = await window.crypto.subtle.importKey(
        "raw",
        keyMaterial,
        {
          name: "HMAC",
          hash: "SHA-256",
        },
        false,
        ["verify"]
      );

      const isValid = await window.crypto.subtle.verify(
        "HMAC",
        hmacKey,
        base64ToArrayBuffer(signature),
        stringToArrayBuffer(ciphertext)
      );

      return isValid;
    } catch (error) {
return false;
    }
  }

  /**
   * Store public key in localStorage
   */
  storePublicKey(publicKey) {
    localStorage.setItem("e2ee_public_key", publicKey);
    this.publicKey = publicKey;
  }

  /**
   * Get public key from localStorage
   */
  getPublicKey() {
    if (this.publicKey) {
      return this.publicKey;
    }
    this.publicKey = localStorage.getItem("e2ee_public_key");
    return this.publicKey;
  }

  /**
   * Check if user has encryption keys
   */
  hasKeys() {
    return (
      !!localStorage.getItem("e2ee_public_key") &&
      !!localStorage.getItem("e2ee_private_key_encrypted")
    );
  }

  /**
   * Clear all cached keys (on logout)
   */
  clearCache() {
    this.privateKeyCache = null;
    this.conversationKeysCache.clear();
}

  /**
   * Clear all stored keys (nuclear option)
   */
  clearAllKeys() {
    localStorage.removeItem("e2ee_public_key");
    localStorage.removeItem("e2ee_private_key_encrypted");
    this.clearCache();
    this.publicKey = null;
}
}

// Create and export singleton instance
const e2eeService = new E2EEService();
export default e2eeService;
